/**
 * The files repository, as somebody managing it sees it.
 *
 * The one decision this screen makes, over and over: **who a file reaches is
 * never a default.** Sharing starts with nothing selected, "Everyone in the
 * portal" is a deliberate choice rather than the first option in a dropdown,
 * and choosing it asks once more before anything is written. The cohorts here
 * are intakes of named candidates; a document meant for one of them appearing
 * in front of all of them is the harm this screen exists to avoid.
 *
 * Everything else is ordinary: upload, name it, say who it is for, and see at
 * a glance what each file already reaches.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  api, ApiError, fileDownloadUrl, fileSize, uploadFile,
  type SharedFile,
} from '../../api';

type Cohort = { id: number; name: string };
type Scope = 'none' | 'portal' | 'cohorts';

const day = (iso: string) =>
  new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

export function AdminFiles() {
  const [files, setFiles] = useState<SharedFile[]>([]);
  const [cohorts, setCohorts] = useState<Cohort[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState('');

  // The sharing form. `scope: 'none'` is the starting state on purpose — there
  // is no first option that quietly means "everyone".
  const [pending, setPending] = useState<{ id: number; name: string } | null>(null);
  const [scope, setScope] = useState<Scope>('none');
  const [chosen, setChosen] = useState<number[]>([]);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const fileInput = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get<{ files: SharedFile[]; cohorts: Cohort[] }>('/admin/files');
      setFiles(res.files);
      setCohorts(res.cohorts);
    } catch (e) { setError((e as ApiError).message); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const reset = () => {
    setPending(null); setScope('none'); setChosen([]); setName(''); setDescription('');
    if (fileInput.current) fileInput.current.value = '';
  };

  const upload = async (file: File) => {
    setBusy(true); setError(null); setNote(null);
    try {
      const up = await uploadFile(file);
      setPending({ id: up.id, name: up.originalName ?? file.name });
      setName(up.originalName ?? file.name);
    } catch (e) { setError((e as ApiError).message); }
    finally { setBusy(false); }
  };

  const doShare = async () => {
    if (!pending || scope === 'none') return;

    if (scope === 'portal') {
      const ok = confirm(
        `Share “${name || pending.name}” with EVERYONE in the portal?\n\n`
        + 'Every trainee, on every intake, will see it and be able to download it. '
        + 'If this document is about particular candidates, choose their cohorts instead.',
      );
      if (!ok) return;
    }

    setBusy(true); setError(null);
    try {
      await api.post('/admin/files', scope === 'portal'
        ? { scope: 'portal', mediaFileId: pending.id, name: name || undefined, description: description || undefined }
        : { scope: 'cohorts', mediaFileId: pending.id, cohortIds: chosen, name: name || undefined, description: description || undefined });
      setNote(scope === 'portal'
        ? 'Shared with everyone in the portal.'
        : `Shared with ${chosen.length} cohort${chosen.length === 1 ? '' : 's'}.`);
      reset();
      await load();
    } catch (e) { setError((e as ApiError).message); }
    finally { setBusy(false); }
  };

  const unshare = async (f: SharedFile) => {
    const who = f.cohortName ? `the “${f.cohortName}” cohort` : 'everyone in the portal';
    if (!confirm(`Stop sharing “${f.name}” with ${who}?\n\nThe file itself is kept.`)) return;
    setBusy(true);
    try {
      const res = await api.del<{ stillUsedBy: { units: number; courseFiles: number } }>(`/admin/files/${f.id}`);
      const still = res.stillUsedBy.units + res.stillUsedBy.courseFiles;
      setNote(still > 0
        ? `Removed from the repository. A course still hands this file out in ${still} place${still === 1 ? '' : 's'}, so it has not left the portal.`
        : 'Removed from the repository.');
      await load();
    } catch (e) { setError((e as ApiError).message); }
    finally { setBusy(false); }
  };

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return files;
    return files.filter((f) =>
      f.name.toLowerCase().includes(needle) || (f.cohortName ?? '').toLowerCase().includes(needle));
  }, [files, q]);

  const canShare = !!pending && (scope === 'portal' || (scope === 'cohorts' && chosen.length > 0));

  return (
    <>
      <h1>Files</h1>
      <p className="muted">
        Documents that are not part of a course — a handbook, a visa checklist, a Prometric
        schedule. Share one with everybody, or with particular intakes.
      </p>

      {error && <p className="auth__error">{error}</p>}
      {note && <p className="notice">{note}</p>}

      <section className="panel">
        <h2 className="h2">Add a file</h2>

        {!pending ? (
          <>
            <input
              ref={fileInput} type="file" disabled={busy}
              onChange={(e) => { const f = e.target.files?.[0]; if (f) void upload(f); }}
            />
            {busy && <p className="muted small">Uploading…</p>}
          </>
        ) : (
          <div className="form">
            <p className="muted small">Uploaded <strong>{pending.name}</strong></p>

            <label><span>Called</span>
              <input value={name} maxLength={255} onChange={(e) => setName(e.target.value)} />
            </label>
            <label><span>What it is (optional)</span>
              <input value={description} maxLength={2000} onChange={(e) => setDescription(e.target.value)} />
            </label>

            <fieldset className="scope">
              <legend>Who is it for?</legend>
              {/* Nothing is selected until somebody selects it. */}
              <label className="check">
                <input
                  type="radio" name="scope" checked={scope === 'cohorts'}
                  onChange={() => setScope('cohorts')}
                />
                Particular cohorts
              </label>
              <label className="check">
                <input
                  type="radio" name="scope" checked={scope === 'portal'}
                  onChange={() => setScope('portal')}
                />
                Everyone in the portal
              </label>

              {scope === 'cohorts' && (
                <div className="checklist">
                  {cohorts.length === 0 && <p className="muted small">There are no cohorts yet.</p>}
                  {cohorts.map((c) => (
                    <label key={c.id} className="check">
                      <input
                        type="checkbox" checked={chosen.includes(c.id)}
                        onChange={(e) => setChosen((was) =>
                          e.target.checked ? [...was, c.id] : was.filter((id) => id !== c.id))}
                      />
                      {c.name}
                    </label>
                  ))}
                </div>
              )}

              {scope === 'portal' && (
                <p className="warn small">
                  Every trainee on every intake will see this. If it is about particular
                  candidates, choose their cohorts instead.
                </p>
              )}
            </fieldset>

            <div className="row">
              <button type="button" disabled={busy || !canShare} onClick={() => void doShare()}>
                {busy ? 'Sharing…' : 'Share it'}
              </button>
              <button type="button" className="btn-danger" disabled={busy} onClick={reset}>
                Cancel
              </button>
            </div>
          </div>
        )}
      </section>

      <div className="filters">
        <input
          value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Search files" aria-label="Search files" size={28}
        />
        <span className="muted small">{shown.length} of {files.length}</span>
      </div>

      <table className="grid">
        <thead>
          <tr><th>File</th><th>Reaches</th><th>Size</th><th>Shared</th><th /></tr>
        </thead>
        <tbody>
          {shown.map((f) => (
            <tr key={f.id}>
              <td>
                <a href={fileDownloadUrl(f.mediaFileId)}>{f.name}</a>
                {f.description && <div className="muted small">{f.description}</div>}
              </td>
              <td>
                {f.cohortName
                  ? f.cohortName
                  : <strong className="warn">Everyone in the portal</strong>}
              </td>
              <td className="num">{fileSize(f.sizeBytes)}</td>
              <td className="small">{day(f.sharedAt)}</td>
              <td>
                <button type="button" className="linkish" disabled={busy} onClick={() => void unshare(f)}>
                  stop sharing
                </button>
              </td>
            </tr>
          ))}
          {shown.length === 0 && (
            <tr><td colSpan={5} className="muted">Nothing here yet.</td></tr>
          )}
        </tbody>
      </table>
    </>
  );
}
